import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Icon, Checkbox } from '@saviynt/design-system';
import { STATUS } from '../../constants/migrationConstants';
import { UI_LABELS } from '../../constants/stringConstants';
import useStyles from './styles/ApplicationSelector.styles';

function ApplicationSelector({ 
  applications, 
  selectedApps, 
  onAppSelect, 
  showApplications, 
  onToggle,
  onSelectAll,
  onClear,
  hasEligible,
  completedLabel,
}) {
  const classes = useStyles();
  
  return (
    <div className={classes.root}>
      <button
        type="button"
        onClick={onToggle}
        className={classes.toggleButton}
      >
        <span>Select applications to migrate data ({selectedApps.length} selected)</span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="currentColor" 
          className={`${classes.chevron} ${showApplications ? classes.chevronOpen : ''}`}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {showApplications && (
        <div className={classes.container}>
          <Typography variant="body2" className={classes.hint}>
            Data will be migrated only for applications that were migrated in Configuration Migration
          </Typography>
          <div className={classes.list}>
            {applications.map(app => {
              const isPending = (app.dataMigrationStatus === STATUS.STATUS_PENDING || app.dataMigrationStatus === undefined || app.dataMigrationStatus === null) && app.status === STATUS.STATUS_COMPLETED;
              const isSelected = selectedApps.includes(app.id);
              const isAuthoritative = /<application[^>]*authoritative="1"/.test(app.sourceRawData || '');
              if (isAuthoritative) return null;
              
              const itemClasses = [
                classes.appItem,
                isSelected && classes.appItemSelected,
                !isPending && classes.appItemDisabled
              ].filter(Boolean).join(' ');
              
              return (
                <div
                  key={app.id}
                  className={itemClasses}
                  onClick={() => isPending && onAppSelect(app.id)}
                  onKeyDown={(e) => e.key === 'Enter' && isPending && onAppSelect(app.id)}
                  role="button"
                  tabIndex={0}
                >
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={isSelected}
                      isDisabled={!isPending}
                      onChange={() => onAppSelect(app.id)}
                    />
                  </div>
                  <Icon kind="Applications" size="small" />
                  <span className={`${classes.appName} ${!isPending ? classes.appNameCompleted : ''}`}>
                    {app.objectName}
                  </span>
                  {isAuthoritative && (
                    <span className={classes.authoritativeBadge}>Authoritative</span>
                  )}
                  {!isPending && (
                    <span className={`${classes.statusBadge} ${classes.statusSuccess}`}>
                      <Icon kind='CheckCircleFilledSeparateColors' label='Circle' color='--color-foreground-success-subtle' size='small' />
                      {completedLabel}
                    </span>
                  )}
                  {isPending && (
                    <span className={`${classes.statusBadge} ${classes.statusPending}`}>
                      <Icon kind='time' label='Circle' color='--color-foreground-warning-subtle' size='small' />
                      Pending
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className={classes.actions}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelectAll();
              }}
              disabled={!hasEligible}
              className={`${classes.actionButton} ${classes.selectAllButton} ${!hasEligible ? classes.actionButtonDisabled : ''}`}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className={`${classes.actionButton} ${classes.clearButton}`}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

ApplicationSelector.propTypes = {
  applications: PropTypes.arrayOf(PropTypes.shape({})),
  selectedApps: PropTypes.arrayOf(PropTypes.string),
  onAppSelect: PropTypes.func,
  showApplications: PropTypes.bool,
  onToggle: PropTypes.func,
  onSelectAll: PropTypes.func,
  onClear: PropTypes.func,
  hasEligible: PropTypes.bool,
  completedLabel: PropTypes.string,
};

ApplicationSelector.defaultProps = {
  applications: [],
  selectedApps: [],
  onAppSelect: undefined,
  showApplications: false,
  onToggle: undefined,
  onSelectAll: undefined,
  onClear: undefined,
  hasEligible: false,
  completedLabel: UI_LABELS.IMPORT_JOB_CREATED,
};

export default ApplicationSelector;
